import { useCallback, useEffect, useRef, useState } from 'react'
import type { EmblaCarouselType } from 'embla-carousel'

type Options = {
  onReachEnd?: () => void
}

const useEmblaNavigation = (
  emblaApi: EmblaCarouselType | undefined,
  { onReachEnd }: Options = {},
) => {
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(false)
  const [singlePage, setSinglePage] = useState(false)
  const onReachEndRef = useRef(onReachEnd)
  onReachEndRef.current = onReachEnd

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setPrevDisabled(!emblaApi.canScrollPrev())
    setNextDisabled(!emblaApi.canScrollNext())

    // Skip the reach-end prefetch when the carousel root has no
    // measurable width — e.g. when the desktop slider is rendered
    // as display:none on mobile viewports inside a `<DesktopOnly>`
    // branch. Embla still initialises against the hidden container,
    // but with zero width the snap math is degenerate (snapCount
    // stays at 0/1) and `selected >= snapCount - 2` is permanently
    // true. Without this guard the reInit event that fires after
    // each fetchMore-driven slide change cascades into a runaway
    // prefetch loop — sc-12228 captured 47 calls in 15s.
    const root = emblaApi.rootNode?.()
    if (root && root.offsetWidth === 0) return

    const snapCount = emblaApi.scrollSnapList().length
    setSinglePage(snapCount <= 1)
    const selected = emblaApi.selectedScrollSnap()
    // Fire when within 1 page of the end
    if (selected >= snapCount - 2 && onReachEndRef.current) {
      onReachEndRef.current()
    }
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return undefined

    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  const handlePrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const handleNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  return { handlePrev, handleNext, prevDisabled, nextDisabled, singlePage }
}

export default useEmblaNavigation
